import {
  CheckMetadata,
  CheckMetadataItem,
  CheckMetadataItemType,
} from 'dashboard/components/ChecksTable/computeCheckMetadata';
import { CheckStatus, SelfieLivenessDetection } from 'dashboard/models/check';
import Sticker from 'design-system/components/Sticker';
import { Variant } from 'design-system/theme';

type SelfieLivenessRiskLevel = NonNullable<SelfieLivenessDetection['metadata']>['risk-level'];

export const computeSelfieLivenessDetectionMetadata = (
  check: SelfieLivenessDetection,
): CheckMetadata => {
  let riskLevel: SelfieLivenessRiskLevel | null = null;

  if (check.metadata && check.reasons) {
    // TODO(lucas): Update this to support multi-reasons as needed.
    const checkReason = check.reasons[0];

    if (check.status === CheckStatus.Passed) {
      riskLevel = check.metadata['risk-level'];
    } else if (checkReason === 'replica_detected') {
      riskLevel = check.metadata['risk-level'];
    } else if (checkReason === 'invalid_pose') {
      riskLevel = 'na';
    }
  }

  let metadata = [] as CheckMetadataItem[];
  const selfieLivenessRiskLevelDisplay = riskLevel
    ? selfieLivenessRiskLevelDisplays[riskLevel]
    : null;

  if (selfieLivenessRiskLevelDisplay) {
    metadata = [
      {
        type: CheckMetadataItemType.Group,
        heading: {
          title: 'Liveness Risk',
          sticker: (
            <Sticker variant={selfieLivenessRiskLevelDisplay.stickerVariant}>
              {selfieLivenessRiskLevelDisplay.stickerText}
            </Sticker>
          ),
        },
        items: [
          {
            label: 'Risk Assessment',
            value: selfieLivenessRiskLevelDisplay.riskAssessmentText,
          },
          {
            label: 'Recommendation',
            value: selfieLivenessRiskLevelDisplay.recommendation,
          },
        ],
      },
    ];
  }

  return {
    metadata,
    hideIfVerificationPassed: false,
  };
};

type SelfieLivenessRiskLevelDisplayData = {
  stickerText: string;
  stickerVariant: Exclude<Variant, 'inverse'>;
  riskAssessmentText: string;
  recommendation: string;
};

const selfieLivenessRiskLevelDisplays: Record<
  SelfieLivenessRiskLevel,
  SelfieLivenessRiskLevelDisplayData
> = {
  ['high']: {
    stickerText: 'High',
    stickerVariant: 'danger',
    riskAssessmentText: 'Image contains features that indicate it is likely a spoof.',
    recommendation: 'Reject',
  },
  ['medium']: {
    stickerText: 'Medium',
    stickerVariant: 'caution',
    riskAssessmentText: 'Image contains features that are often indicative of a spoof.',
    recommendation: 'Manually Review',
  },
  ['low']: {
    stickerText: 'Low',
    stickerVariant: 'success',
    riskAssessmentText: 'Image contains elevated features that may indicate a spoof.',
    recommendation: 'Manually Review for High Value Transactions',
  },
  ['minimal']: {
    stickerText: 'Minimal',
    stickerVariant: 'success',
    riskAssessmentText: 'Image does not contain enough suspicious elements that indicate a spoof.',
    recommendation: 'Pass',
  },
  ['na']: {
    stickerText: 'NA',
    stickerVariant: 'basic',
    riskAssessmentText: 'Image does not contain a high enough quality selfie submission.',
    recommendation: 'Retry',
  },
};
